import { useFetchTenantSetting } from 'hooks/dataAccess/Tenant/tenantSetting';
import React, { ReactNode, createContext, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';

interface TenantContextValue {
    /** The partner ID of the tenant. */
    partnerId: string | null;
    /** The client ID of the tenant. */
    clientId: string | null;
    /** Whether the tenant uses QBR or CBR jargon. */
    qbrToCBR: string;
}

const TenantContext = createContext<TenantContextValue | null>(null);

interface TenantProviderProps {
    /** The children of the tenant provider. */
    children: ReactNode;
    /** The partner ID of the tenant. */
    partnerId?: string | null;
    /** The client ID of the tenant. */
    clientId?: string | null;
}

export const TenantProvider = ({
    children,
    partnerId: propPartnerId,
    clientId: propClientId
}: TenantProviderProps) => {
    const { partnerId: urlPartnerId, clientId: urlClientId } = useParams();

    const { setting: qbrToCBR } = useFetchTenantSetting({
        clientId: propClientId ?? urlClientId ?? undefined,
        key: 'qbrToCBR'
    });

    const value = useMemo(
        () => ({
            clientId: propClientId ?? urlClientId ?? null,
            partnerId: propPartnerId ?? urlPartnerId ?? null,
            qbrToCBR: qbrToCBR ? 'cbr' : 'qbr'
        }),
        [propPartnerId, propClientId, qbrToCBR, urlPartnerId, urlClientId]
    );

    return (
        <TenantContext.Provider value={value}>
            {children}
        </TenantContext.Provider>
    );
};

export const useTenant = () => {
    const context = useContext(TenantContext);
    if (context === null) {
        throw new Error('useTenant must be used within a TenantProvider');
    }
    return context;
};
